import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { FormHelperText, Grid, Icon } from "@mui/material";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDBox from "components/MDBox";
import { AddNewBlog, uploadImageBlog } from "APIs/BlogAPIs";
import { UserContext } from "App";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import FullScreenLoader from "components/Custom/FullScreenLoader";
import { notify } from "App";
// Receipt page theme (shared)
import SectionCard, {
  SectionHeader,
  ranuButtonSx,
  ranuOutlinedButtonSx,
  useAccent,
} from "layouts/receipt/components/SectionCard";
import FormField from "layouts/receipt/components/FormField";

// Back button: maroon (theme primary) outline and text, arrow icon on the left
const backButtonSx = ({ palette }) => ({
  color: palette.primary.main,
  borderColor: palette.primary.main,
  backgroundColor: "transparent",
  borderRadius: "10px",
  boxShadow: "none",
  boxSizing: "border-box",
  height: { xs: "2.25rem", md: "2.5rem" },
  minHeight: { xs: "2.25rem", md: "2.5rem" },
  px: 2.5,
  fontSize: "0.8rem",
  fontWeight: 700,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  gap: 0.75,
  transition: "background-color .3s ease, color .3s ease, border-color .3s ease",
  // Hover: gold (theme secondary), same as the other outlined buttons
  "&:hover": {
    color: palette.secondary.main,
    borderColor: palette.secondary.main,
    backgroundColor: "transparent",
    boxShadow: "none",
  },
  "&:focus:not(:hover)": { boxShadow: "none" },
});

// The Save button lives outside the <form>, so it points to the form by this id
const FORM_ID = "add-blog-form";

// Required fields (checked on blur and when Save is clicked)
const FIELDS = [
  { name: "title", label: "Title" },
  { name: "description", label: "Description" },
];

const getFieldError = ({ name, label }, data) =>
  String(data?.[name] ?? "").trim() === "" ? `${label} is required` : "";

const IMAGE_REQUIRED_MSG = "At least one image is required.";

// Thumbnail frame. `isNew` = image selected but not uploaded yet (gold border).
const tileSx =
  (isNew = false) =>
  ({ palette, borders }) => ({
    position: "relative",
    display: "block",
    width: "100%",
    height: 140,
    overflow: "hidden",
    textDecoration: "none",
    borderRadius: borders.borderRadius.lg,
    backgroundColor: palette.light.main,
    border: isNew
      ? `2px solid ${palette.secondary.main}`
      : `${borders.borderWidth[1]} solid ${borders.borderColor}`,
  });

// Image with a round "x" button in the corner
const ImageTile = ({ src, alt, isNew, onRemove }) => (
  <MDBox sx={tileSx(isNew)}>
    <img
      src={src}
      alt={alt}
      style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
    />
    <MDBox
      component="button"
      type="button"
      onClick={onRemove}
      aria-label="Remove image"
      sx={({ palette }) => ({
        position: "absolute",
        top: 6,
        right: 6,
        width: 26,
        height: 26,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "none",
        borderRadius: "50%",
        cursor: "pointer",
        fontSize: 14,
        fontWeight: 700,
        color: palette.white.main,
        backgroundColor: palette.error.main,
        transition: "background-color .3s ease",
        "&:hover": { backgroundColor: palette.primary.main },
      })}
    >
      ✕
    </MDBox>
  </MDBox>
);

ImageTile.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  isNew: PropTypes.bool,
  onRemove: PropTypes.func.isRequired,
};

ImageTile.defaultProps = {
  alt: "blog_img",
  isNew: false,
};

// Native file input (same accept list and behaviour as before), themed
const UploadInput = ({ onChange }) => (
  <MDBox
    component="input"
    type="file"
    name="image"
    multiple
    accept=".png,.jpg,.jpeg,.gif,.webp,.tiff,.psd,.bmp,.ico,.icon"
    onChange={onChange}
    sx={({ palette, borders }) => ({
      width: "100%",
      padding: "10px",
      cursor: "pointer",
      fontSize: 14,
      color: palette.text.main,
      border: `1px dashed ${palette.secondary.main}`,
      borderRadius: borders.borderRadius.lg,
      "&::file-selector-button": {
        marginRight: "12px",
        padding: "6px 14px",
        border: "none",
        borderRadius: borders.borderRadius.md,
        cursor: "pointer",
        color: palette.white.main,
        backgroundColor: palette.primary.main,
        transition: "background-color .3s ease",
      },
      "&::file-selector-button:hover": { backgroundColor: palette.secondary.main },
    })}
  />
);

UploadInput.propTypes = {
  onChange: PropTypes.func.isRequired,
};

const AddBlog = () => {
  const navigate = useNavigate();
  const userData = useContext(UserContext);
  const dispatch = useDispatch();
  const { accentName } = useAccent();

  const [blogData, setBlogData] = useState({
    title: "",
    description: "",
  });

  // State management for newly selected images ({ id, file, preview })
  const [newFiles, setNewFiles] = useState([]);

  // Image is compulsory: error is shown in red under the upload input
  const [imageError, setImageError] = useState("");

  // Full-screen loader state
  const [loading, setLoading] = useState(false);

  const [touched, setTouched] = useState({});
  const formRef = useRef(null);
  const [scrollToErrorKey, setScrollToErrorKey] = useState(0);

  // After the errors have been rendered, scroll to the first one
  useEffect(() => {
    if (!scrollToErrorKey) return;
    formRef.current
      ?.querySelector(".Mui-error")
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [scrollToErrorKey]);

  const handleBlur = (name) => setTouched((prev) => ({ ...prev, [name]: true }));

  // An error only appears after the field is touched (blurred, or Save was clicked)
  const getVisibleError = (field) => (touched[field.name] ? getFieldError(field, blogData) : "");

  const isFormValid = FIELDS.every((field) => !getFieldError(field, blogData));

  const handleBlogDataChange = (e) => {
    setBlogData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // Handle selecting new files and creating local preview URLs
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);
      const newImageObjects = selectedFiles.map((file) => ({
        id: Math.random().toString(36).substring(2, 9),
        file: file,
        preview: URL.createObjectURL(file),
      }));

      setNewFiles((prev) => [...prev, ...newImageObjects]);
      setImageError("");
      e.target.value = ""; // Reset input so same file can be re-selected if removed
    }
  };

  // Delete newly selected image before submission
  const handleDeleteNewImage = (fileId) => {
    setNewFiles((prev) => {
      const fileToRemove = prev.find((item) => item.id === fileId);
      if (fileToRemove) {
        URL.revokeObjectURL(fileToRemove.preview); // Cleanup browser memory
      }
      return prev.filter((item) => item.id !== fileId);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return; // prevent double submit

    const hasNoImage = newFiles.length === 0;

    // Invalid form or no image: show every error (missing fields + image) and scroll to the first
    if (!isFormValid || hasNoImage) {
      if (hasNoImage) setImageError(IMAGE_REQUIRED_MSG);
      if (!isFormValid) {
        setTouched(Object.fromEntries(FIELDS.map((field) => [field.name, true])));
      }
      setScrollToErrorKey((key) => key + 1);
      return;
    }

    setLoading(true);
    try {
      const AddBlogRes = await AddNewBlog(blogData, userData.access_token, dispatch);

      if (AddBlogRes !== null) {
        if (newFiles.length > 0) {
          let formData = new FormData();
          formData.append("blog_id", AddBlogRes.blog_id);
          newFiles.forEach((item) => {
            formData.append("files", item.file);
          });

          await uploadImageBlog(formData, userData.access_token, dispatch);
        }
        notify("Blog added!", "success");
        navigate("/blog");
      }
      console.log(AddBlogRes);
    } catch (error) {
      console.error(error);
    } finally {
      // Always hide loader, even if an API call fails
      setLoading(false);
    }
  };

  return (
    <MDBox px={{ xs: 1, md: 3 }} pt={{ xs: 0, md: 1 }} pb={3}>
      <FullScreenLoader open={loading} />

      {/* Back button sits right beside the page badge (startAction = left of the badge) */}
      <SectionHeader
        icon="post_add"
        label="ADD BLOG"
        startAction={
          <MDButton
            type="button"
            variant="outlined"
            color="primary"
            sx={backButtonSx}
            onClick={() => navigate("/blog")}
          >
            <Icon sx={{ fontSize: "1.1rem !important", fontWeight: "bold" }}>arrow_back</Icon>
            Back
          </MDButton>
        }
      />

      <form ref={formRef} id={FORM_ID} onSubmit={handleSubmit} noValidate autoComplete="off">
        <SectionCard title="Blog" subtitle="Fields marked * are required">
          <Grid container spacing={2}>
            {/* Title Input */}
            <FormField name="title" required full>
              <MDInput
                name="title"
                type="text"
                placeholder="Enter title"
                fullWidth
                value={blogData.title}
                onChange={handleBlogDataChange}
                onBlur={() => handleBlur(FIELDS[0].name)}
                error={!!getVisibleError(FIELDS[0])}
              />
              {getVisibleError(FIELDS[0]) && (
                <FormHelperText error>{getVisibleError(FIELDS[0])}</FormHelperText>
              )}
            </FormField>

            {/* Description Input */}
            <FormField name="description" required full>
              <MDInput
                name="description"
                multiline
                rows={6}
                placeholder="Enter description"
                fullWidth
                value={blogData.description}
                onChange={handleBlogDataChange}
                onBlur={() => handleBlur(FIELDS[1].name)}
                error={!!getVisibleError(FIELDS[1])}
              />
              {getVisibleError(FIELDS[1]) && (
                <FormHelperText error>{getVisibleError(FIELDS[1])}</FormHelperText>
              )}
            </FormField>
          </Grid>
        </SectionCard>

        <SectionCard title="Media" subtitle="At least one image is required">
          <Grid container spacing={2}>
            {/* Image Previews Section */}
            {newFiles.length > 0 && (
              <Grid item xs={12}>
                <MDTypography
                  variant="button"
                  fontWeight="bold"
                  color={accentName}
                  display="block"
                  mb={1.5}
                >
                  Selected Images ({newFiles.length})
                </MDTypography>
                <Grid container spacing={2}>
                  {newFiles.map((item) => (
                    <Grid item key={item.id} xs={6} sm={4} md={3}>
                      <ImageTile
                        isNew
                        src={item.preview}
                        alt="selected_preview"
                        onRemove={() => handleDeleteNewImage(item.id)}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}

            {/* Media Upload Input */}
            <FormField name="upload_media" required full>
              <UploadInput onChange={handleFileChange} />
              {imageError && <FormHelperText error>{imageError}</FormHelperText>}
            </FormField>
          </Grid>
        </SectionCard>
      </form>

      {/* Cancel + Save sit outside the form: bottom right on desktop, centered on mobile.
          Save still submits the form through the `form` attribute. */}
      <MDBox display="flex" gap={1.5} justifyContent={{ xs: "center", md: "flex-end" }}>
        <MDButton
          type="button"
          variant="outlined"
          color="primary"
          sx={ranuOutlinedButtonSx}
          disabled={loading}
          onClick={() => navigate("/blog")}
        >
          Cancel
        </MDButton>
        <MDButton color="primary" type="submit" form={FORM_ID} sx={ranuButtonSx} disabled={loading}>
          Save Blog
        </MDButton>
      </MDBox>
    </MDBox>
  );
};

export default AddBlog;
